import React, { useState } from 'react';
import { 
  Calendar, 
  Plus, 
  ChevronLeft, 
  ChevronRight, 
  Clock, 
  MapPin, 
  Users,
  Filter,
  Search
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';

interface CalendarEvent {
  id: string;
  title: string;
  date: string;
  time: string;
  location?: string;
  attendees?: string[];
  type: 'appointment' | 'meeting' | 'reminder' | 'travel' | 'other';
  color: string;
}

export function MobileCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('month');
  const [searchQuery, setSearchQuery] = useState('');

  // Mock events data
  const events: CalendarEvent[] = [
    {
      id: '1',
      title: 'Doctor Appointment',
      date: '2024-01-15',
      time: '10:00 AM',
      location: 'Medical Center',
      type: 'appointment',
      color: 'bg-red-500'
    },
    {
      id: '2',
      title: 'Family Meeting',
      date: '2024-01-15',
      time: '2:00 PM',
      attendees: ['John', 'Sarah', 'Mike'],
      type: 'meeting',
      color: 'bg-blue-500'
    },
    {
      id: '3',
      title: 'Pet Vaccination',
      date: '2024-01-16',
      time: '3:30 PM',
      location: 'Vet Clinic',
      type: 'appointment',
      color: 'bg-orange-500'
    },
    {
      id: '4',
      title: 'Travel Departure',
      date: '2024-01-18',
      time: '8:00 AM',
      location: 'Airport',
      type: 'travel',
      color: 'bg-green-500'
    }
  ];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    return { daysInMonth, startingDay };
  };

  const getEventsForDate = (date: string) => {
    return events.filter(event => event.date === date);
  };

  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const getMonthName = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const getDayName = (date: Date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'appointment':
        return <Clock size={14} />;
      case 'meeting':
        return <Users size={14} />;
      case 'travel':
        return <MapPin size={14} />;
      default:
        return <Calendar size={14} />;
    }
  };

  const renderMonthView = () => {
    const { daysInMonth, startingDay } = getDaysInMonth(currentDate);
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-12"></div>);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dateString = formatDate(date);
      const dayEvents = getEventsForDate(dateString);
      const isToday = formatDate(new Date()) === dateString;
      const isSelected = formatDate(selectedDate) === dateString;
      
      days.push(
        <div
          key={day}
          className={`h-12 border border-gray-200 p-1 cursor-pointer ${
            isToday ? 'bg-idecide-primary text-white' : ''
          } ${isSelected ? 'ring-2 ring-idecide-primary' : ''}`}
          onClick={() => setSelectedDate(date)}
        >
          <div className="text-xs font-medium">{day}</div>
          {dayEvents.length > 0 && (
            <div className="flex gap-1 mt-1">
              {dayEvents.slice(0, 2).map((event, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full ${event.color}`}
                  title={event.title}
                />
              ))}
              {dayEvents.length > 2 && (
                <div className="text-xs text-gray-500">+{dayEvents.length - 2}</div>
              )}
            </div>
          )}
        </div>
      );
    }
    
    return days;
  };

  const renderSelectedDateEvents = () => {
    const dateString = formatDate(selectedDate);
    const dayEvents = getEventsForDate(dateString);
    
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900">
          {selectedDate.toLocaleDateString('en-US', { 
            weekday: 'long', 
            month: 'long', 
            day: 'numeric' 
          })}
        </h3>
        {dayEvents.length === 0 ? (
          <div className="text-center py-8">
            <Calendar size={48} className="text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No events scheduled</p>
          </div>
        ) : (
          <div className="space-y-3">
            {dayEvents.map((event) => (
              <Card key={event.id} className="p-4">
                <div className="flex items-start gap-3">
                  <div className={`w-3 h-3 rounded-full ${event.color} mt-1`} />
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{event.title}</h4>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                      <Clock size={14} />
                      <span>{event.time}</span>
                    </div>
                    {event.location && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                        <MapPin size={14} />
                        <span>{event.location}</span>
                      </div>
                    )}
                    {event.attendees && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                        <Users size={14} />
                        <span>{event.attendees.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Calendar</h2>
          <p className="text-sm text-gray-600">{getMonthName(currentDate)}</p>
        </div>
        <Button size="sm" onClick={() => console.log('Add event')}>
          <Plus size={16} className="mr-2" />
          Add Event
        </Button>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const newDate = new Date(currentDate);
            newDate.setMonth(newDate.getMonth() - 1);
            setCurrentDate(newDate);
          }}
        >
          <ChevronLeft size={16} />
        </Button>
        
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'month' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('month')}
          >
            Month
          </Button>
          <Button
            variant={viewMode === 'week' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('week')}
          >
            Week
          </Button>
          <Button
            variant={viewMode === 'day' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('day')}
          >
            Day
          </Button>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const newDate = new Date(currentDate);
            newDate.setMonth(newDate.getMonth() + 1);
            setCurrentDate(newDate);
          }}
        >
          <ChevronRight size={16} />
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search events..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-idecide-primary focus:border-transparent"
        />
      </div>

      {/* Calendar Grid */}
      {viewMode === 'month' && (
        <div className="bg-white rounded-lg shadow-sm">
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-px bg-gray-200">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="bg-gray-50 p-2 text-center text-xs font-medium text-gray-600">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-px bg-gray-200">
            {renderMonthView()}
          </div>
        </div>
      )}

      {/* Selected Date Events */}
      {viewMode === 'month' && (
        <div className="bg-white rounded-lg p-4 shadow-sm">
          {renderSelectedDateEvents()}
        </div>
      )}

      {/* Upcoming Events */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <h3 className="text-md font-semibold text-gray-900 mb-3">Upcoming Events</h3>
        <div className="space-y-3">
          {events.slice(0, 3).map((event) => (
            <div key={event.id} className="flex items-center gap-3 p-2 rounded bg-gray-50">
              <div className={`w-3 h-3 rounded-full ${event.color}`} />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{event.title}</p>
                <p className="text-xs text-gray-500">
                  {new Date(event.date).toLocaleDateString()} at {event.time}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <Button variant="outline" className="w-full">
          <Plus size={16} className="mr-2" />
          Add Event
        </Button>
        <Button variant="outline" className="w-full">
          <Filter size={16} className="mr-2" />
          Filter
        </Button>
      </div>
    </div>
  );
} 